import { useEffect, useRef } from 'react';
import { useChatContext } from '../chat-view/context/use-chat-context';
import { SuperbarWindowApi, type ChatContext } from './superbar-window-api';
import { useGetStoreInfo } from '../chat-view/hooks/useGetStoreInfo';

/**
 * React hook that initializes and registers the Superbar API on the window object.
 *
 * @remarks
 * This hook creates an instance of {@link SuperbarWindowApi} and exposes it globally
 * via `window.superbar`. It handles the lifecycle of the API, automatically cleaning up
 * when the component unmounts. The API provides methods for external integrations to
 * control the Superbar's state and display messages.
 *
 * This hook should be called at the root level of your Superbar component to ensure
 * the API is available throughout the application lifecycle.
 *
 * @example
 * ```typescript
 * function SuperbarRoot() {
 *   useSuperbarWindowApi();
 *
 *   return <div>Your Superbar UI...</div>;
 * }
 * ```
 *
 * @example
 * External scripts can then use the API:
 * ```typescript
 * // In any external script
 * if (window.superbar) {
 *   await window.superbar.expand({ source: 'external_trigger' });
 *   window.superbar.sendMessage('Welcome to our store!');
 * }
 * ```
 */
export function useSuperbarWindowApi(): void {
  const chatContext = useChatContext();
  const { currentSession, store } = useGetStoreInfo();

  const chatContextRef = useRef(chatContext);
  const sessionRef = useRef(currentSession);
  const storeRef = useRef(store);

  chatContextRef.current = chatContext;
  sessionRef.current = currentSession;
  storeRef.current = store;

  useEffect(() => {
    // Check if superbar is already defined
    if (window.superbar) {
      // API already exists, don't recreate
      return;
    }

    const chatContext: ChatContext = {
      setDisplayMessage: (message) =>
        chatContextRef.current.setDisplayMessage(message),
      setDisplayMode: (mode) => chatContextRef.current.setDisplayMode(mode),
      setSuperBarViewState: (state) =>
        chatContextRef.current.setSuperBarViewState(state),
      getSuperBarViewState: () => chatContextRef.current.superBarViewState,
      clearAll: () => chatContextRef.current.clearAll(),
      get currentSession() {
        return sessionRef.current;
      },
      get currentStore() {
        return storeRef.current;
      },
    };

    const superbarApi = new SuperbarWindowApi(chatContext);

    // Define superbar API with security protection
    // Using defineProperty prevents external scripts from overwriting the API
    try {
      Object.defineProperty(window, 'superbar', {
        value: superbarApi,
        writable: false,
        configurable: false,
        enumerable: true,
      });
    } catch (error) {
      console.error('[Superbar API] Failed to define window.superbar:', error);
    }

    return () => {
      // Note: Due to configurable: false, we cannot delete the property
      // This is intentional for security. The API instance will be garbage collected
      // when the component unmounts, but the property descriptor remains.
    };
    // Only run once on mount
  }, []);
}
